import type { MentionItem } from "@/components/prompt-editor/types"
import {
  characterWorkspaceHref,
  locationWorkspaceHref,
  propWorkspaceHref,
  scriptWorkspaceHref,
  shotWorkspaceHref,
} from "@/lib/job-subject-label"
import { taskPersonLabel, taskPersonName } from "@/lib/task-board"
import type {
  TaskMentionEntity,
  TaskMentionEntityKey,
  TaskMentionEntityKind,
  TaskMentionPerson,
  TaskMentionSearchRow,
  TaskMentionTaskLink,
  TaskMentionTaskRef,
  TaskMentions,
  TaskPerson,
} from "@/types/tasks"

export type TaskMentionTokenCategory = "file" | "person" | "task" | TaskMentionEntityKind

export const TASK_MENTION_ENTITY_KINDS: readonly TaskMentionEntityKind[] = ["hero", "loc", "prop", "shot", "script"]

// Подуровень через двоеточие бывает только у сущностей: у @file, @user, @task и @script его нет;
// у @shot сервер подуровень не читает.
export const TASK_MENTION_START_RE =
  /@(?:(?:file|user|task|script)\[\d{1,18}\]|(?:hero|loc|prop|shot)\[\d{1,18}(?::\d{1,18})?\])/

const TASK_MENTION_TOKEN_RE =
  /^@(?:(file|user|task|script)\[(\d{1,18})\]|(hero|loc|prop|shot)\[(\d{1,18})(?::(\d{1,18}))?\])/
const TASK_MENTION_IDS_RE = /@(?:(user|task|script)\[(\d{1,18})\]|(hero|loc|prop|shot)\[(\d{1,18})(?::(\d{1,18}))?\])/g

export function isTaskMentionEntityKind(category: string): category is TaskMentionEntityKind {
  return (
    category === "hero" || category === "loc" || category === "prop" || category === "shot" || category === "script"
  )
}

function tokenCategory(kind: string): "file" | "person" | "task" | "script" {
  if (kind === "user") return "person"
  if (kind === "script") return "script"
  return kind === "task" ? "task" : "file"
}

export function parseTaskMentionToken(
  src: string,
): { raw: string; category: TaskMentionTokenCategory; id: number; stateId: number | null } | null {
  const match = TASK_MENTION_TOKEN_RE.exec(src)
  if (!match) return null
  if (match[1] != null) return { raw: match[0], category: tokenCategory(match[1]), id: Number(match[2]), stateId: null }
  return {
    raw: match[0],
    category: match[3] as TaskMentionEntityKind,
    id: Number(match[4]),
    stateId: match[5] == null ? null : Number(match[5]),
  }
}

export function taskMentionToken(category: string, id: number, stateId: number | null = null): string {
  if (category === "person") return `@user[${id}]`
  if (category === "task") return `@task[${id}]`
  if (category === "script") return `@script[${id}]`
  if (isTaskMentionEntityKind(category))
    return stateId == null ? `@${category}[${id}]` : `@${category}[${id}:${stateId}]`
  return `@file[${id}]`
}

export function taskMentionEntityKey(kind: TaskMentionEntityKind, id: number, subId: number | null): string {
  return `${kind}:${id}:${subId ?? ""}`
}

export function taskMentionIds(markdown: string): {
  people: number[]
  tasks: number[]
  entities: TaskMentionEntityKey[]
} {
  const people = new Set<number>()
  const tasks = new Set<number>()
  const entities = new Map<string, TaskMentionEntityKey>()
  for (const match of markdown.matchAll(TASK_MENTION_IDS_RE)) {
    if (match[1] != null) {
      if (match[1] === "script") {
        const key: TaskMentionEntityKey = { kind: "script", id: Number(match[2]), sub_id: null }
        const mapKey = taskMentionEntityKey(key.kind, key.id, key.sub_id)
        if (!entities.has(mapKey)) entities.set(mapKey, key)
        continue
      }
      const ids = match[1] === "user" ? people : tasks
      ids.add(Number(match[2]))
      continue
    }
    const key: TaskMentionEntityKey = {
      kind: match[3] as TaskMentionEntityKind,
      id: Number(match[4]),
      sub_id: match[5] == null || match[3] === "shot" ? null : Number(match[5]),
    }
    const mapKey = taskMentionEntityKey(key.kind, key.id, key.sub_id)
    if (!entities.has(mapKey)) entities.set(mapKey, key)
  }
  return { people: [...people], tasks: [...tasks], entities: [...entities.values()] }
}

// entitiesLoaded — в entities лежит весь список проекта: сущности, которой в нём нет, уже не существует.
export type TaskMentionDirectory = {
  people: ReadonlyMap<number, TaskMentionPerson>
  tasks: ReadonlyMap<number, TaskMentionTaskRef>
  entities: ReadonlyMap<string, TaskMentionEntity>
  entitiesLoaded: boolean
  noteAssetsLoaded: boolean
}

export const EMPTY_TASK_MENTIONS: TaskMentions = { people: [], tasks: [], entities: [] }

export function mergeTaskMentions(...lists: TaskMentions[]): TaskMentions {
  const { people, tasks, entities } = taskMentionDirectory(...lists)
  return { people: [...people.values()], tasks: [...tasks.values()], entities: [...entities.values()] }
}

export function taskMentionDirectory(...lists: TaskMentions[]): TaskMentionDirectory {
  return withTaskMentions(
    { people: new Map(), tasks: new Map(), entities: new Map(), entitiesLoaded: false, noteAssetsLoaded: false },
    ...lists,
  )
}

/** Каталог заметки: ростер компании, затем сохранённые упоминания, затем ответ поиска. */
export function noteMentionDirectory(
  companyPeople: readonly TaskMentionPerson[],
  saved: TaskMentions,
  found: TaskMentions,
): TaskMentionDirectory {
  return {
    ...taskMentionDirectory({ people: [...companyPeople], tasks: [], entities: [] }, saved, found),
    entitiesLoaded: true,
    noteAssetsLoaded: true,
  }
}

export function mentionEntityWorkspaceHref(
  projectId: number,
  kind: TaskMentionEntityKind,
  id: number,
  subId: number | null,
): string {
  if (kind === "hero") return characterWorkspaceHref(projectId, id, subId)
  if (kind === "loc") return locationWorkspaceHref(projectId, id, subId)
  if (kind === "prop") return propWorkspaceHref(projectId, id, subId)
  if (kind === "shot") return shotWorkspaceHref(projectId, id)
  return scriptWorkspaceHref(projectId)
}

export function withTaskMentions(directory: TaskMentionDirectory, ...lists: TaskMentions[]): TaskMentionDirectory {
  const people = new Map(directory.people)
  const tasks = new Map(directory.tasks)
  const entities = new Map(directory.entities)
  for (const list of lists) {
    for (const person of list.people) people.set(person.user_id, person)
    for (const task of list.tasks) tasks.set(task.id, task)
    // Ответ сервера без entities (старая версия) — то же, что пустой список.
    const listed: TaskMentionEntity[] | undefined = list.entities
    for (const entity of listed ?? []) entities.set(taskMentionEntityKey(entity.kind, entity.id, entity.sub_id), entity)
  }
  return {
    people,
    tasks,
    entities,
    entitiesLoaded: directory.entitiesLoaded,
    noteAssetsLoaded: directory.noteAssetsLoaded,
  }
}

type EntityKindTexts = Record<TaskMentionEntityKind, string>

export type TaskMentionTexts = {
  unnamed: string
  taskNoAccess: string
  taskDeleted: string
  taskSearchHint: string
  taskSearching: string
  openPerson: string
  openTask: string
  entityDeleted: EntityKindTexts
  entitySubDeleted: EntityKindTexts
  openEntity: EntityKindTexts
}

export const TASK_MENTION_PENDING = "…"

export function taskMentionTaskLabel(ref: TaskMentionTaskLink): string {
  return `${ref.key} ${ref.title}`
}

export function taskMentionDisplay(
  directory: TaskMentionDirectory,
  category: "person" | "task",
  id: number,
  nodeLabel: string,
  texts: TaskMentionTexts,
): { text: string; missing: boolean } {
  if (category === "person") {
    const person = directory.people.get(id)
    if (person) return { text: `@${taskPersonLabel(person, texts.unnamed)}`, missing: false }
    if (nodeLabel !== "") return { text: `@${nodeLabel}`, missing: false }
    return { text: `@${texts.unnamed}`, missing: true }
  }
  const ref = directory.tasks.get(id)
  if (ref?.state === "ok") return { text: taskMentionTaskLabel(ref), missing: false }
  if (ref?.state === "no_access") return { text: texts.taskNoAccess, missing: true }
  if (ref?.state === "deleted") return { text: texts.taskDeleted, missing: true }
  if (nodeLabel !== "") return { text: nodeLabel, missing: false }
  return { text: TASK_MENTION_PENDING, missing: true }
}

// null — ещё неизвестно: расшифровки нет, а список проекта не загружен.
export function resolveTaskMentionEntity(
  directory: TaskMentionDirectory,
  kind: TaskMentionEntityKind,
  id: number,
  subId: number | null,
): TaskMentionEntity | null {
  const ref = directory.entities.get(taskMentionEntityKey(kind, id, subId))
  if (ref) return ref
  // Сценарий не входит в список сущностей проекта: без расшифровки заметки не считаем его удалённым.
  if (kind === "script" && !directory.noteAssetsLoaded) return null
  if (!directory.entitiesLoaded) return null
  const parent = subId == null ? undefined : directory.entities.get(taskMentionEntityKey(kind, id, null))
  if (parent?.state === "no_access") return { kind, id, sub_id: subId, state: "no_access" }
  if (parent && parent.state !== "deleted") return { ...parent, sub_id: subId, sub_name: null, state: "sub_deleted" }
  return { kind, id, sub_id: subId, state: "deleted" }
}

function entityName(ref: { name: string; sub_id: number | null; sub_name: string | null }): string {
  return ref.sub_id != null && ref.sub_name ? `${ref.name} · ${ref.sub_name}` : ref.name
}

export function taskMentionEntityLabel(ref: TaskMentionEntity, texts: TaskMentionTexts): string {
  if (ref.state === "deleted") return texts.entityDeleted[ref.kind]
  if (ref.state === "no_access") return texts.taskNoAccess
  if (ref.state === "sub_deleted") return `${ref.name} · ${texts.entitySubDeleted[ref.kind]}`
  return entityName(ref)
}

export type TaskMentionEntityDisplay = {
  text: string
  missing: boolean
  state: TaskMentionEntity["state"] | "pending"
  imageUrl: string | null
  frontViewChrome: boolean
}

export function taskMentionEntityDisplay(
  directory: TaskMentionDirectory,
  kind: TaskMentionEntityKind,
  id: number,
  subId: number | null,
  nodeLabel: string,
  texts: TaskMentionTexts,
): TaskMentionEntityDisplay {
  const ref = resolveTaskMentionEntity(directory, kind, id, subId)
  if (!ref) {
    const text = nodeLabel !== "" ? `@${nodeLabel}` : TASK_MENTION_PENDING
    return { text, missing: nodeLabel === "", state: "pending", imageUrl: null, frontViewChrome: false }
  }
  const label = taskMentionEntityLabel(ref, texts)
  if (ref.state === "deleted" || ref.state === "no_access") {
    return { text: label, missing: true, state: ref.state, imageUrl: null, frontViewChrome: false }
  }
  return {
    text: `@${label}`,
    missing: ref.state !== "ok",
    state: ref.state,
    imageUrl: ref.thumb_url,
    frontViewChrome: ref.thumb_url != null && ref.front_view,
  }
}

export function taskMentionDirectoryKey(directory: TaskMentionDirectory): string {
  const people = [...directory.people.values()].map((person) => [
    person.user_id,
    taskPersonLabel(person, ""),
    person.on_team,
  ])
  const tasks = [...directory.tasks.values()].map((ref) =>
    ref.state === "ok" ? [ref.id, ref.state, ref.key, ref.title] : [ref.id, ref.state],
  )
  const entities = [...directory.entities.values()].map((ref) =>
    ref.state === "deleted" || ref.state === "no_access"
      ? [ref.kind, ref.id, ref.sub_id, ref.state]
      : [
          ref.kind,
          ref.id,
          ref.sub_id,
          ref.state,
          ref.name,
          ref.sub_name,
          ref.subtitle,
          ref.thumb_url,
          ref.front_view,
          ref.project_id ?? null,
        ],
  )
  return JSON.stringify([people, tasks, entities, directory.entitiesLoaded, directory.noteAssetsLoaded])
}

export function personMentionItem(person: TaskPerson, unnamed: string): MentionItem {
  return {
    category: "person",
    id: person.user_id,
    label: taskPersonLabel(person, unnamed),
    subtitle: person.email,
    person: taskPersonName(person),
  }
}

export function taskMentionItem(row: TaskMentionSearchRow): MentionItem {
  return { category: "task", id: row.id, label: taskMentionTaskLabel(row), subtitle: row.project_name }
}

// Плоский список меню в порядке сервера: сущность строкой, её образы, варианты или состояния — строками под ней.
export function entityMentionItems(rows: readonly TaskMentionEntity[]): MentionItem[] {
  return rows.flatMap((row) => {
    if (row.state !== "ok") return []
    return {
      category: row.kind,
      id: row.id,
      stateId: row.sub_id,
      label: entityName(row),
      subtitle: row.sub_id == null ? row.subtitle : null,
      imageUrl: row.thumb_url,
      frontViewChrome: row.thumb_url != null && row.front_view,
    }
  })
}
